import { AccessLevel } from '@dev/ee-api/ee-api-models';
import Home from '@api/components/Home';
import MyAccount from '@api/components/MyAccount';
import Legal from '@api/components/Legal';
import Editor from '@api/components/Editor';
import Settings from '@api/components/Settings/Overview';
import TrackingAndPruning from '@api/components/Settings/TrackingAndPruning';
import Advanced from '@api/components/Settings/Advanced';
import SystemOptions from '@api/components/Settings/SystemOptions';
import Unsubscribe from '@api/components/Settings/Unsubscribe';
import Domains from '@api/components/Settings/Domains';
import SuppressionsTabs from '@api/components/Suppressions';
import AddSuppressions from '@api/components/Suppressions/AddSuppression';
import ManagePrivateIPs from '@api/components/Settings/PrivateIP';
import SingleIpView from '@api/components/Settings/PrivateIP/SingleIpView';
import ManagePools from '@api/components/Settings/PrivateIP/ManagePools';
import AddAccessTokens from '@api/components/Settings/accessTokens/AddAccessToken';
import ManageAccessTokens from '@api/components/Settings/accessTokens/ManageAccessTokens';
import ManageInboundRoutes from '@api/components/Settings/InboundRoutes';
import SingleInbound from '@api/components/Settings/InboundRoutes/SingleInbound';
import ManageWebhooks from '@api/components/Settings/Webhooks';
import SingleWebhook from '@api/components/Settings/Webhooks/SingleWebhook';
import StatisticsTabs from '@api/components/Statistics';
import SingleChannelTabs from '@api/components/Statistics/Channels/SingleChannel';
import LinkContacts from '@api/components/Statistics/Channels/SingleChannel/LinkTracking/LinkContacts';
import StatisticsInProgress from '@api/components/Statistics/InProgress';
import Exports from '@api/components/Exports';
import { LogsPaths, SettingsPaths, SuppressionsPaths, StatisticsPaths, VerificationPaths, UpsellingPaths, SurveysPaths } from './paths';
import Logs from '@api/components/Logs';
import { MarketingUpselling } from '@api/components/MarketingUpselling';
import DomainsAdvanced from '@api/components/Settings/DomainsAdvanced';
import { EvsSingleFile, EvsVerificationsTabs, EvsSingleLead } from '../../dashboards/Evs';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import { TemplatesPaths } from '@api/dashboards/Marketing/paths';
import { OnboardingSurvey } from '@api/components/Surveys/OnboardingSurvey';

interface RouteInterface {
    path: string | string[];
    component: React.ReactNode;
    accessLevel: AccessLevel[];
    disableBanners?: boolean;
    upsellingFeature?: AvailableFeaturesUpselling;
}

export const VerificationRoutes: RouteInterface[] = [
    { path: VerificationPaths.BulkVerification, component: EvsVerificationsTabs, accessLevel: [AccessLevel.VerifyEmails] },
    { path: VerificationPaths.VerificationsTabs, component: EvsVerificationsTabs, accessLevel: [AccessLevel.VerifyEmails] },
    { path: VerificationPaths.SingleFile, component: EvsSingleFile, accessLevel: [AccessLevel.VerifyEmails] },
    { path: VerificationPaths.SingleLead, component: EvsSingleLead, accessLevel: [AccessLevel.VerifyEmails] },
];

export const GeneralRoutes: RouteInterface[] = [
    { path: '/', component: Home, accessLevel: [AccessLevel.ViewAccount] },
    { path: '/my-account', component: MyAccount, accessLevel: [AccessLevel.ViewAccount] },
    { path: '/my-account/legal', component: Legal, accessLevel: [AccessLevel.ViewAccount] },
];

export const SuppressionsRoutes: RouteInterface[] = [
    { path: SuppressionsPaths.Suppressions, component: SuppressionsTabs, accessLevel: [AccessLevel.ViewSuppressions] },
    { path: SuppressionsPaths.UnsubscribedSuppressions, component: SuppressionsTabs, accessLevel: [AccessLevel.ViewSuppressions] },
    {
        path: SuppressionsPaths.SuppressionsAdd,
        component: AddSuppressions,
        accessLevel: [AccessLevel.ViewSuppressions],
    },
];

export const RawEditorRoutes: RouteInterface[] = [
    {
        path: TemplatesPaths.TemplateEditorName,
        component: Editor,
        accessLevel: [AccessLevel.ModifyTemplates],
        disableBanners: true,
    },
];

export const ExportRoutes: RouteInterface[] = [{ path: '/exports', component: Exports, accessLevel: [AccessLevel.Export] }];

export const StatisticsRoutes: RouteInterface[] = [
    { path: StatisticsPaths.StatisticsOverview, component: StatisticsTabs, accessLevel: [AccessLevel.ViewReports] },
    { path: StatisticsPaths.StatisticsInprogress, component: StatisticsInProgress, accessLevel: [AccessLevel.ViewReports] },
    { path: StatisticsPaths.StatisticsLog, component: StatisticsInProgress, accessLevel: [AccessLevel.ViewReports] },
    { path: StatisticsPaths.StatisticsChannels, component: StatisticsTabs, accessLevel: [AccessLevel.ViewReports] },
    { path: StatisticsPaths.StatisticsDeviceDetails, component: StatisticsTabs, accessLevel: [AccessLevel.ViewReports] },
    { path: StatisticsPaths.AllLinkTracking, component: StatisticsTabs, accessLevel: [AccessLevel.ViewReports] },
    {
        path: StatisticsPaths.StatisticsChannelOverview,
        component: SingleChannelTabs,
        accessLevel: [AccessLevel.ViewReports],
    },
    {
        path: StatisticsPaths.StatisticsChannelLogs,
        component: SingleChannelTabs,
        accessLevel: [AccessLevel.ViewReports],
    },
    {
        path: StatisticsPaths.StatisticsChannelLinkTracking,
        component: SingleChannelTabs,
        accessLevel: [AccessLevel.ViewReports],
    },
    {
        path: StatisticsPaths.StatisticsChannelLinkTrackingContacts,
        component: LinkContacts,
        accessLevel: [AccessLevel.ViewReports],
    },
    {
        path: StatisticsPaths.AllLinkTrackingSingleLinkContacts,
        component: LinkContacts,
        accessLevel: [AccessLevel.ViewReports],
    },
];

export const LogsRoutes: RouteInterface[] = [
    {
        path: LogsPaths.Logs,
        component: Logs,
        accessLevel: [AccessLevel.ViewReports],
    },
];

export const SettingsRoutes: RouteInterface[] = [
    { path: SettingsPaths.Settings, component: Settings, accessLevel: [AccessLevel.ViewSettings] },
    {
        path: SettingsPaths.SystemOptions,
        component: SystemOptions,
        accessLevel: [AccessLevel.ViewSettings],
    },

    {
        path: SettingsPaths.TrackingAndPruning,
        component: TrackingAndPruning,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.Advanced,
        component: Advanced,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.Unsubscribe,
        component: Unsubscribe,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: [
            SettingsPaths.Domains,
            SettingsPaths.DomainsDomainVerification,
            SettingsPaths.DomainsEmailVerification,
            SettingsPaths.SingleDomainVerification,
            SettingsPaths.EmailAddressVerification,
            SettingsPaths.SingleDomainBounceDomain,
        ],
        component: Domains,
        accessLevel: [AccessLevel.ViewSettings],
    },
    { path: SettingsPaths.DomainsAdvanced, component: DomainsAdvanced, accessLevel: [AccessLevel.ViewSettings] },
    {
        path: SettingsPaths.CreateSMTP,
        component: AddAccessTokens,
        accessLevel: [AccessLevel.ModifySettings],
    },
    {
        path: SettingsPaths.ManageSMTP,
        component: ManageAccessTokens,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.EditSMTP,
        component: AddAccessTokens,
        accessLevel: [AccessLevel.ModifySettings],
    },
    {
        path: SettingsPaths.CreateAPI,
        component: AddAccessTokens,
        accessLevel: [AccessLevel.ModifySettings],
    },
    {
        path: SettingsPaths.EditAPI,
        component: AddAccessTokens,
        accessLevel: [AccessLevel.ModifySettings],
    },
    {
        path: SettingsPaths.ManageAPI,
        component: ManageAccessTokens,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.ManagePrivateIPs,
        component: ManagePrivateIPs,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.SingleIP,
        component: SingleIpView,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.ManagePools,
        component: ManagePools,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.ManageInbounds,
        component: ManageInboundRoutes,
        accessLevel: [AccessLevel.ViewWebNotifications],
        upsellingFeature: AvailableFeaturesUpselling.InboundEmails,
    },
    {
        path: SettingsPaths.CreateInbound,
        component: SingleInbound,
        accessLevel: [AccessLevel.ViewWebNotifications],
        upsellingFeature: AvailableFeaturesUpselling.InboundEmails,
    },
    {
        path: SettingsPaths.EditInbound,
        component: SingleInbound,
        accessLevel: [AccessLevel.ViewWebNotifications],
        upsellingFeature: AvailableFeaturesUpselling.InboundEmails,
    },
    {
        path: SettingsPaths.ManageWebhooks,
        component: ManageWebhooks,
        accessLevel: [AccessLevel.ViewWebNotifications],
        upsellingFeature: AvailableFeaturesUpselling.Webhooks,
    },
    {
        path: SettingsPaths.CreateWebhook,
        component: SingleWebhook,
        accessLevel: [AccessLevel.ViewWebNotifications],
        upsellingFeature: AvailableFeaturesUpselling.Webhooks,
    },
    {
        path: SettingsPaths.EditWebhook,
        component: SingleWebhook,
        accessLevel: [AccessLevel.ViewWebNotifications],
        upsellingFeature: AvailableFeaturesUpselling.Webhooks,
    },
];
export const MarketingUpsellingRoutes: RouteInterface[] = [
    {
        path: UpsellingPaths.MarketingUpselling,
        component: MarketingUpselling,
        accessLevel: [AccessLevel.ModifyBilling],
    },
    {
        path: UpsellingPaths.MarketingHomeUpselling,
        component: MarketingUpselling,
        accessLevel: [AccessLevel.ModifyBilling],
    },
];

export const OnboardingSurveyRoutes: RouteInterface[] = [
    {
        path: SurveysPaths.Onboarding,
        component: OnboardingSurvey,
        accessLevel: [AccessLevel.ViewAccount],
    },
];
